type TestFunction = (value: unknown) => boolean;

export class Validator {
  private tests: { testFn: TestFunction; errorMessage: string }[] = [];

  private errors: string[] = [];

  addTest(testFunction: TestFunction, errorMessage: string): this {
    this.tests.push({ testFn: testFunction, errorMessage });
    return this;
  }

  required(errorMessage: string = 'This field is required'): this {
    return this.addTest(
      (value) => value !== undefined && value !== null && value !== '',
      errorMessage,
    );
  }

  alphaNumericWithSymbols(
    errorMessage: string = 'Value must only contain alphanumeric characters, underscores, and @',
  ): this {
    return this.addTest(
      (value) => typeof value === 'string' && /^[\w@]*$/.test(value),
      errorMessage,
    );
  }

  alphaNumericWithUnderscores(
    errorMessage: string = 'Value must only contain alphanumeric characters and underscores',
  ): this {
    return this.addTest((value) => typeof value === 'string' && /^\w+$/.test(value), errorMessage);
  }

  min(
    minValue: number,
    errorMessage: string = `Value must be greater than or equal to ${minValue}`,
  ): this {
    return this.addTest((value) => typeof value === 'number' && value >= minValue, errorMessage);
  }

  max(
    maxValue: number,
    errorMessage: string = `Value must be less than or equal to ${maxValue}`,
  ): this {
    return this.addTest((value) => typeof value === 'number' && value <= maxValue, errorMessage);
  }

  minLength(
    minValue: number,
    errorMessage: string = `Length must be greater than or equal to ${minValue}`,
  ): this {
    return this.addTest(
      (value) => (typeof value === 'string' && value.length >= minValue) || value === '',
      errorMessage,
    );
  }

  maxLength(
    maxValue: number,
    errorMessage: string = `Length must be less than or equal to ${maxValue}`,
  ): this {
    return this.addTest(
      (value) => typeof value === 'string' && value.length <= maxValue,
      errorMessage,
    );
  }

  email(errorMessage: string = 'Invalid email format'): this {
    return this.addTest(
      (value) => typeof value === 'string' && /^\S+@\S+\.\S+$/.test(value),
      errorMessage,
    );
  }

  phoneNumber(errorMessage: string = 'Invalid phone number format'): this {
    return this.addTest(
      (value) => typeof value === 'string' && /^\d{10}$/.test(value),
      errorMessage,
    );
  }

  string(errorMessage: string = 'Value must be a string'): this {
    return this.addTest((value) => typeof value === 'string', errorMessage);
  }

  number(errorMessage: string = 'Value must be a number'): this {
    return this.addTest((value) => typeof value === 'number', errorMessage);
  }

  validate(value: unknown): boolean {
    this.errors = [];
    for (const { testFn, errorMessage } of this.tests) {
      if (!testFn(value)) {
        this.errors.push(errorMessage);
      }
    }
    return this.errors.length === 0;
  }

  noLeadingOrTrailingSpaces(errorMessage: string = 'Cannot have leading or trailing spaces'): this {
    return this.addTest(
      (value) => typeof value === 'string' && value === value.trim(),
      errorMessage,
    );
  }

  getErrors(): string[] {
    return this.errors;
  }
}
export const string = (): Validator => new Validator().string();
export const number = (): Validator => new Validator().number();

export const validate = (
  schema: { [key: string]: Validator },
  values: { [key: string]: unknown },
): { valid: boolean; errors: { [key: string]: string[] } } => {
  const errors: { [key: string]: string[] } = {};
  let valid = true;

  for (const key of Object.keys(schema)) {
    const validator = schema[key];
    const value = values[key];

    if (!validator.validate(value)) {
      valid = false;
      errors[key] = validator.getErrors();
    }
  }

  return { valid, errors };
};
